import {
  CourseSelectionAction,
  CourseSelectionActionTypes,
  CourseSelectionState,
  SelectedSection,
} from '../types/courseSelectionTypes';
import { formatSectionError, SectionsErrors } from '../utils/parseErrors';

export const initialState: CourseSelectionState = {
  student: null,
  campusCareer: null,
  selectedCampus: undefined,
  selectedCourse: null,
  equivalenceOriginCourse: null,
  selectedSections: [],
  selectedCredits: 0,
  creditsPeriod: {
    creditMin: 0,
    creditMax: 0,
    creditConfigurationEnabled: false,
    creditsContracted: null,
  },
  selectedCareer: null,
  currentEvent: undefined,
  error: null,
};

const courseSelectionReducer = (
  state: CourseSelectionState,
  action: CourseSelectionAction,
): CourseSelectionState => {
  switch (action.type) {
    case CourseSelectionActionTypes.RESET_STATE:
      return {
        ...initialState,
      };

    case CourseSelectionActionTypes.SET_CAMPUS_CAREER:
      return {
        ...state,
        campusCareer: action.payload,
      };

    case CourseSelectionActionTypes.SET_COURSE:
      return {
        ...state,
        selectedCourse: action.payload,
        equivalenceOriginCourse:
          action.payload === null ? null : state.equivalenceOriginCourse,
      };

    case CourseSelectionActionTypes.SET_EQUIVALENCE_ORIGIN:
      return {
        ...state,
        equivalenceOriginCourse: action.payload,
      };

    case CourseSelectionActionTypes.ADD_SECTION:
      // Valida que no existan conflictos en el listado
      if (state?.currentEvent?.allowSectionScheduleConflicts === false) {
        const hasScheduleConflicts = state.selectedSections
          // Filtrar las secciones del mismo curso
          .filter((s) => s.course.id !== action.payload.course.id)
          // Encontrar la primera sección que tenga algún horario en conflicto
          .find((selected) =>
            selected.sectionSchedules.some((sch1) =>
              action.payload.sectionSchedules.some((sch2) => {
                if (
                  (sch1.day || '').toUpperCase() !==
                  (sch2.day || '').toUpperCase()
                ) {
                  return false;
                }

                const start1 = sch1.startModule?.startTime || '00:00:00';
                const end1 = sch1.endModule?.endTime || start1;
                const start2 = sch2.startModule?.startTime || '00:00:00';
                const end2 = sch2.endModule?.endTime || start2;

                return start1 < end2 && start2 < end1;
              }),
            ),
          );

        if (hasScheduleConflicts) {
          return {
            ...state,
            error: {
              code: SectionsErrors.CONFLICT_SECTION_SCHEDULE,
              message: JSON.stringify([
                formatSectionError(hasScheduleConflicts),
                formatSectionError(action.payload),
              ]),
            },
          };
        }
      }

      // Valida que no exista anteriormente en el listado
      if (state.selectedSections.some((s) => s.id === action.payload.id)) {
        return { ...state, error: null };
      } else {
        // Se busca si existe seccion seleccionada de la misma asignatura
        const removeByCourse = state.selectedSections.find(
          (s) => s.course.id === action.payload.course.id,
        );
        // HU 15417: si la sección se está agregando mientras hay una asignatura
        // ORIGEN activa (flujo de equivalencia) y la sección pertenece a una
        // asignatura distinta a la origen (la equivalente), estampamos el FK
        // en la sección. Esto evita perder la traza si el estudiante limpia
        // el state global (p.ej. al hacer "Volver") antes de enviar.
        const enrichedPayload: SelectedSection = {
          ...action.payload,
          ...(state.equivalenceOriginCourse &&
          action.payload.course.id !== state.equivalenceOriginCourse.id
            ? {
                enrolledByEquivalenceCourseId: state.equivalenceOriginCourse.id,
              }
            : {}),
        };
        // Se filtra el listado removiendo otras secciones seleccionadas de la asignatura
        // y se inserta la nueva sección seleccionada
        const newSections = [
          ...state.selectedSections.filter(
            (section) => section.course.id !== action.payload.course.id,
          ),
          enrichedPayload,
        ].sort((a, b) => {
          // Se ordena el array con la nueva sección agregada
          // de forma ascendente por la sigla de la asignatura
          if (a.course.code.toUpperCase() > b.course.code.toUpperCase()) {
            return 1;
          } else if (
            a.course.code.toUpperCase() < b.course.code.toUpperCase()
          ) {
            return -1;
          } else {
            return 0;
          }
        });

        return {
          ...state,
          error: null,
          selectedSections: newSections,
          // Se suman créditos sólo si no había sección seleccionada anteriormente
          // de lo contrario como las secciones tienen la misma cantidad de créditos
          // se mantiene igual
          selectedCredits:
            state.selectedCredits +
            (!removeByCourse ? action.payload.course.credits : 0),
        };
      }

    case CourseSelectionActionTypes.ADD_SECTION_ALLOWING_REPEATED:
      // Valida que no existan conflictos en el listado
      if (state?.currentEvent?.allowSectionScheduleConflicts === false) {
        const hasScheduleConflicts = state.selectedSections
          // Filtrar las secciones del mismo curso
          .filter((s) => s.course.id !== action.payload.course.id)
          // Encontrar la primera sección que tenga algún horario en conflicto
          .find((selected) =>
            selected.sectionSchedules.some((sch1) =>
              action.payload.sectionSchedules.some((sch2) => {
                if (
                  (sch1.day || '').toUpperCase() !==
                  (sch2.day || '').toUpperCase()
                ) {
                  return false;
                }

                const start1 = sch1.startModule?.startTime || '00:00:00';
                const end1 = sch1.endModule?.endTime || start1;
                const start2 = sch2.startModule?.startTime || '00:00:00';
                const end2 = sch2.endModule?.endTime || start2;

                return start1 < end2 && start2 < end1;
              }),
            ),
          );

        if (hasScheduleConflicts) {
          return {
            ...state,
            error: {
              code: SectionsErrors.CONFLICT_SECTION_SCHEDULE,
              message: JSON.stringify([
                formatSectionError(hasScheduleConflicts),
                formatSectionError(action.payload),
              ]),
            },
          };
        }
      }

      // Valida que no exista anteriormente en el listado
      if (state.selectedSections.some((s) => s.id === action.payload.id)) {
        return { ...state, error: null };
      } else {
        // Se busca si existe seccion seleccionada de la misma asignatura
        const removeByCourse = state.selectedSections.find(
          (s) => s.course.id === action.payload.course.id,
        );
        // HU 15417: ver comentario en ADD_SECTION. Mismo predicate para
        // estampar el FK en la sección que se está agregando.
        const enrichedPayload: SelectedSection = {
          ...action.payload,
          ...(state.equivalenceOriginCourse &&
          action.payload.course.id !== state.equivalenceOriginCourse.id
            ? {
                enrolledByEquivalenceCourseId: state.equivalenceOriginCourse.id,
              }
            : {}),
        };
        // Se filtra el listado removiendo otras secciones seleccionadas de la asignatura
        // y se inserta la nueva sección seleccionada
        const newSections = [...state.selectedSections, enrichedPayload].sort(
          (a, b) => {
            // Se ordena el array con la nueva sección agregada
            // de forma ascendente por la sigla de la asignatura
            if (a.course.code.toUpperCase() > b.course.code.toUpperCase()) {
              return 1;
            } else if (
              a.course.code.toUpperCase() < b.course.code.toUpperCase()
            ) {
              return -1;
            } else {
              return 0;
            }
          },
        );

        return {
          ...state,
          error: null,
          selectedSections: newSections,
          // Se suman créditos sólo si no había sección seleccionada anteriormente
          // de lo contrario como las secciones tienen la misma cantidad de créditos
          // se mantiene igual
          selectedCredits:
            state.selectedCredits +
            (!removeByCourse ? action.payload.course.credits : 0),
        };
      }

    case CourseSelectionActionTypes.REMOVE_SECTION:
      const sectionRemove = state.selectedSections.find(
        (s) => s.id === action.payload,
      );
      return {
        ...state,
        selectedSections: state.selectedSections.filter(
          (section) => section.id !== action.payload,
        ),
        selectedCredits:
          state.selectedCredits - (sectionRemove?.course.credits ?? 0),
      };

    case CourseSelectionActionTypes.SELECT_CAMPUS:
      return {
        ...state,
        selectedCampus: action.payload,
      };

    case CourseSelectionActionTypes.REMOVE_ALL_SECTIONS:
      return {
        ...state,
        selectedSections: [],
        selectedCredits: 0,
      };

    case CourseSelectionActionTypes.SET_CREDITS_PERIOD:
      return {
        ...state,
        creditsPeriod: action.payload,
      };

    case CourseSelectionActionTypes.SET_STUDENT_INFO:
      return {
        ...state,
        student: action.payload,
      };

    case CourseSelectionActionTypes.SET_SELECTED_CAREER:
      return {
        ...state,
        selectedCareer: action.payload,
      };

    case CourseSelectionActionTypes.SET_CURRENT_EVENT:
      return {
        ...state,
        currentEvent: action.payload,
      };

    case CourseSelectionActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case CourseSelectionActionTypes.REMOVE_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

export default courseSelectionReducer;
